import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import api from "../../../../services/api";
import Pagination from "@material-ui/lab/Pagination";
import RowsDropdown from "../../../../components/RowsDropdown";
import { useAuth } from "../../../../contexts/AuthContext";
import Header from "../../../../components/ContextTable/Header/Header";

const DateTypes = [
  { name: "created_at", label: "Criado em" },
  { name: "service_date", label: "Data do serviço" }
];

// Sem data inicial a API traz só o ano corrente; o histórico mostra todo o período
const HISTORY_BEGIN_DATE = "2000-01-01";

const EquipmentPreventiveTable = ({
  equipmentId,
  defaultOrder = -1,
  defaultOrderBy = "created_at"
}) => {
  const dt = useRef(null);
  const toast = useRef(null);
  const { access_type } = useAuth();

  const [preventives, setPreventives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState(defaultOrder);
  const [beginDate, setBeginDate] = useState(HISTORY_BEGIN_DATE);
  const [endDate, setEndDate] = useState("");
  const [rows, setRows] = useState(10);
  const [counter, setCounter] = useState(0);
  const [isActive, setIsActive] = useState(1);
  const [dateType, setDateType] = useState(DateTypes[0].name);
  const [totalRecords, setTotalRecords] = useState(0);

  const handleSort = (sortField, sortOrder = 1) => {
    setDateType(sortField);
    setOrder(sortOrder);
    setOrderBy(sortField);
  };

  const fetchPreventiveData = async (endpoint) => {
    const response = await api.get(
      `${endpoint}?page=${page}&search=${search}&beginDate=${beginDate}&endDate=${endDate}&rows=${rows}&orderBy=${orderBy}&order=${order}&multiEquips=${equipmentId}`
    );
    return response.data;
  };

  const sortByDate = (data) => {
    return data.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return order === 1 ? dateA - dateB : dateB - dateA;
    });
  };

  const fetchPreventives = async () => {
    setLoading(true);
    try {
      // "preventive/eletric" já retorna elétricas e combustão juntas
      const data = await fetchPreventiveData("preventive/eletric");

      setPreventives(sortByDate(data.data || []));
      setTotalRecords(data.total || 0);
    } catch (error) {
      console.error("Erro ao buscar preventivas:", error);
      setPreventives([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const getQueryObject = () => ({
    search,
    page,
    orderBy,
    beginDate,
    endDate,
    rows,
    setCounter,
    order,
    id: equipmentId,
    is_active: isActive
  });

  const handleDownloadReport = (rowData, type) => {
    window.open(`/pdf/${type};${rowData.id}`, "_blank").focus();
  };

  const handleDownloadBudget = (rowData, type) => {
    window.open(`/pdf/orcamento;${rowData.id};${type}`, "_blank").focus();
  };

  const actionBodyTemplate = (rowData) => {
    const isGLP =
      rowData.preventive_type === "EmpilhadeiraCombustao" ||
      rowData.preventive_type === "EmpilhadeiraEletricaDeContrapeso";
    const reportType = isGLP ? "preventive-combustion" : "preventive";
    const budgetType = isGLP ? "preventiveCombustion" : "preventive";

    return (
      <React.Fragment>
        {rowData?.parts?.length > 0 && (
          <Button
            icon="pi pi-money-bill"
            style={{
              backgroundColor: "green",
              outline: 0,
              border: "none",
              marginLeft: 6
            }}
            className="p-button-rounded p-button text-white"
            onClick={() => handleDownloadBudget(rowData, budgetType)}
          />
        )}
        <Button
          icon="pi pi-file-pdf"
          tooltip="Download"
          tooltipOptions={{ position: "top" }}
          style={{
            backgroundColor: "#7f0b0b",
            outline: 0,
            border: "none",
            marginLeft: 6
          }}
          className="p-button-rounded p-button text-white"
          onClick={() => handleDownloadReport(rowData, reportType)}
        />
      </React.Fragment>
    );
  };

  const renderPreventiveType = (preventiveType) => {
    const typeArr = preventiveType.match(/[A-Z][a-z]+/g) || [preventiveType];
    return typeArr
      .map((type) => {
        if (type === "Combustao") return "Combustão";
        if (type === "Eletrica") return "Elétrica";
        return type;
      })
      .join(" ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Sem data";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  useEffect(() => {
    if (equipmentId) {
      fetchPreventives();
    }
  }, [equipmentId, page, rows, search, beginDate, endDate, orderBy, order]);

  if (loading) {
    return (
      <div className="card">
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="text-center">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "2rem" }}
            ></i>
            <p className="mt-3">Carregando preventivas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <DataTable
        ref={dt}
        value={preventives}
        dataKey="id"
        rows={rows}
        sortOrder={order}
        sortField={orderBy}
        onSort={({ sortField, sortOrder }) => handleSort(sortField, sortOrder)}
        emptyMessage="Nenhum registro encontrado"
        header={
          <Header
            sortOrder={order}
            setOrder={setOrder}
            setOrderBy={setOrderBy}
            orderBy={orderBy}
            setDateType={setDateType}
            dateType={dateType}
            ordenar={handleSort}
            type="Preventive"
            title="Preventivas"
            dateTypes={DateTypes.map((type) => ({
              label: type.label,
              value: type.name
            }))}
            {...getQueryObject()}
            setIsActive={setIsActive}
          />
        }
        className="p-datatable-responsive-demo"
      >
        <Column headerStyle={{ width: "3rem" }} />
        <Column
          headerStyle={{ width: "10rem" }}
          field="report"
          header="Relatório"
          body={(rowData) => {
            // Preventivas antigas podem não ter número de relatório
            const report = rowData?.auto_report ?? rowData?.report;
            return report ? String(report).padStart(6, 0) : "-";
          }}
        />
        <Column field="company.name" header="Empresa" />
        <Column
          field="preventive_type"
          header="Tipo"
          body={(rowData) => renderPreventiveType(rowData.preventive_type)}
        />
        <Column
          field="service_date"
          header="Data"
          body={(rowData) => formatDate(rowData.date)}
        />
        <Column
          field="hour_meter"
          header="Horímetro"
          body={(rowData) => (rowData?.hour_meter !== undefined && rowData?.hour_meter !== null && rowData?.hour_meter !== "" ? rowData.hour_meter : "-")}
        />
        <Column field="created_by.name" header="Criado por" />
        <Column header="Ações" body={actionBodyTemplate} />
      </DataTable>
      <div className="d-flex justify-content-center align-items-center p-3">
        <div>
          <Pagination
            className="pagination"
            color="primary"
            boundaryCount={1}
            count={Math.ceil(totalRecords / rows)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
          />
        </div>
        <RowsDropdown rows={rows} setRows={setRows} />
      </div>
    </div>
  );
};

export default EquipmentPreventiveTable;
